import classNames from 'classnames'

export type ButtonVariant =
  | 'primary'
  | 'primary-high-contrast'
  | 'secondary'
  | 'secondary-blue'
  | 'secondary-purple'
  | 'tertiary'
  | 'quiet'
  | 'destructive'
  | 'custom'

const baseClasses = 'font-weight-semibold border-small'

export const disabledClasses = classNames(
  baseClasses,
  'background-alpha-shade-55 shade-30 border-shade-45'
)

export const buttonVariantsClasses: Record<
  ButtonVariant,
  { base: string; default: string; disabled: string; isActive: string }
> = {
  primary: {
    base: baseClasses,
    default: classNames(
      'border-transparent white shadow-small-on-hover',
      'background-primary background-primary-dark-on-hover background-primary-dark-on-active'
    ),
    isActive: classNames(
      'border-transparent white shadow-small-on-hover',
      'background-primary-dark'
    ),
    disabled: disabledClasses,
  },
  'primary-high-contrast': {
    base: baseClasses,
    default: classNames(
      'border-transparent white shadow-small-on-hover shade-100-on-dark-mode',
      'background-shade-00 background-shade-05-on-hover'
    ),
    isActive: classNames(
      'border-transparent white shadow-small-on-hover shade-100-on-dark-mode',
      'background-shade-00'
    ),
    disabled: disabledClasses,
  },
  secondary: {
    base: baseClasses,
    default: classNames(
      'border-alpha-shade-45',
      'shade-10 background-alpha-shade-55',
      'background-alpha-shade-50-on-hover background-alpha-shade-45-on-active'
    ),
    disabled: disabledClasses,
    isActive: classNames(
      'border-alpha-shade-45',
      'shade-10 background-alpha-shade-45'
    ),
  },
  'secondary-blue': {
    base: baseClasses,
    default: classNames(
      'border-alpha-blue-45',
      'blue-20 background-alpha-blue-50',
      'background-alpha-blue-45-on-hover background-alpha-blue-45-on-active'
    ),
    isActive: classNames(
      'border-alpha-blue-45',
      'blue-20 background-alpha-blue-45'
    ),
    disabled: disabledClasses,
  },
  'secondary-purple': {
    base: baseClasses,
    default: classNames(
      'border-alpha-purple-45',
      'purple-20 background-alpha-purple-50',
      'background-alpha-purple-45-on-hover background-alpha-purple-45-on-active'
    ),
    disabled: disabledClasses,
    isActive: classNames(
      'border-alpha-purple-45',
      'purple-20 background-alpha-purple-45'
    ),
  },
  tertiary: {
    base: baseClasses,
    default: classNames(
      'border-transparent',
      'shade-10 background-transparent',
      'background-alpha-shade-50-on-hover background-alpha-shade-45-on-active'
    ),
    disabled: classNames(
      baseClasses,
      'background-transparent shade-40 border-transparent'
    ),
    isActive: 'background-shade-45 border-shade-45 shade-20',
  },
  // same as tertiary but with lighter text color
  quiet: {
    base: baseClasses,
    default: classNames(
      'border-transparent',
      'shade-30 background-transparent',
      'background-alpha-shade-50-on-hover background-alpha-shade-45-on-active'
    ),
    disabled: classNames(
      baseClasses,
      'background-transparent shade-40 border-transparent'
    ),
    isActive: 'background-shade-45 border-shade-45 shade-10',
  },
  destructive: {
    base: baseClasses,
    default: classNames(
      'red-40 background-alpha-red-50 border-alpha-red-40',
      'background-alpha-red-45-on-hover background-alpha-red-45-on-active'
    ),
    isActive: classNames(
      'red-40 background-alpha-red-45',
      'border-alpha-red-40'
    ),
    disabled: classNames(disabledClasses, 'border-transparent'),
  },

  custom: {
    base: classNames(baseClasses, 'border-transparent'),
    default: '',
    disabled: disabledClasses,
    isActive: '',
  },
} as const

export const buttonSizesValues = {
  smaller: 24,
  small: 28,
  normal: 32,
  medium: 36,
  large: 44,
} as const

export const buttonSizesClasses: Record<string, string> = {
  smaller: 'padding-vertical-2 padding-horizontal-4 line-height-small',
  small: 'padding-vertical-2 padding-horizontal-8 line-height-larger',
  normal: 'padding-vertical-4 padding-horizontal-12 line-height-larger',
  medium: 'padding-vertical-8 padding-horizontal-12',
  large: 'padding-vertical-12 padding-horizontal-16',
} as const
